import { useState } from 'react';
import { GitCompare, ArrowRight, Crown, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ComparisonResults } from './ComparisonResults';
import { trackEvent } from '../lib/analytics';

interface ComparisonData {
  url1: {
    url: string;
    overallScore: number;
    metrics: {
      structuredData: number;
      mobileFriendly: number;
      accessibility: number;
      contentQuality: number;
    };
    strengths: string[];
    weaknesses: string[];
  };
  url2: {
    url: string;
    overallScore: number;
    metrics: {
      structuredData: number;
      mobileFriendly: number;
      accessibility: number;
      contentQuality: number;
    };
    strengths: string[];
    weaknesses: string[];
  };
}

export function ComparisonPage() {
  const { user, profile } = useAuth();
  const [url1, setUrl1] = useState('');
  const [url2, setUrl2] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);

  // Check if user has access (Professional, Business, or Enterprise)
  const hasAccess = () => {
    if (!user) return false;
    const tier = profile?.subscriptionTier || 'free';
    return ['professional', 'business', 'enterprise'].includes(tier);
  };

  const canUseFeature = hasAccess();

  const normalizeUrl = (raw: string): string => {
    const trimmed = raw.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const handleCompare = async () => {
    if (!canUseFeature) {
      setError('This feature is only available for Professional, Business, and Enterprise plans');
      return;
    }

    if (!url1.trim() || !url2.trim()) {
      setError('Please enter both URLs');
      return;
    }

    const normalizedUrl1 = normalizeUrl(url1);
    const normalizedUrl2 = normalizeUrl(url2);

    setIsAnalyzing(true);
    setError('');
    setComparisonData(null);

    trackEvent('comparison_started', 'comparison', `${normalizedUrl1}_vs_${normalizedUrl2}`);

    try {
      const response = await fetch('/api/comparison/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({ url1: normalizedUrl1, url2: normalizedUrl2 }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Comparison failed');
      }

      const data = await response.json();
      setComparisonData(data);
      trackEvent('comparison_completed', 'comparison', `${url1}_vs_${url2}`);
    } catch (err: any) {
      console.error('Comparison error:', err);
      const errorMessage = err?.message || err?.error || 'Unable to connect to server. Please try again.';
      setError(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleUpgrade = () => {
    trackEvent('upgrade_clicked', 'comparison', 'from_comparison_page');
    window.location.href = '/?pricing=true';
  };

  const handleReset = () => {
    setComparisonData(null);
    setError('');
    setUrl1('');
    setUrl2('');
  };

  if (comparisonData) {
    return <ComparisonResults data={comparisonData} onReset={handleReset} />;
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-black text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
              <GitCompare className="w-4 h-4" />
              AI Readiness Comparison
            </div>

            <h1 className="text-5xl font-normal text-black mb-4 leading-tight">
              Compare Two Websites
            </h1>

            <p className="text-xl text-gray-600 mb-2">
              Analyze and compare AI readiness scores side-by-side
            </p>

            {!canUseFeature && (
              <div className="mt-6 bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 max-w-2xl mx-auto">
                <div className="flex items-start gap-3">
                  <Crown className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                  <div className="text-left">
                    <h3 className="font-medium text-gray-900 mb-2">
                      Premium Feature
                    </h3>
                    <p className="text-sm text-gray-700 mb-4">
                      Website comparison is available for Professional, Business, and Enterprise plans.
                      Upgrade to unlock this powerful feature and compare unlimited websites.
                    </p>
                    <button
                      onClick={handleUpgrade}
                      className="bg-black text-white font-medium py-2 px-6 rounded-lg hover:bg-gray-800 transition-colors inline-flex items-center gap-2"
                    >
                      <Crown className="w-4 h-4" />
                      Upgrade Now
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={`${!canUseFeature ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Website URL
                  </label>
                  <input
                    type="text"
                    value={url1}
                    onChange={(e) => setUrl1(e.target.value)}
                    placeholder="e.g. example.com"
                    disabled={!canUseFeature || isAnalyzing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>

                <div className="flex items-center justify-center">
                  <div className="bg-gray-100 rounded-full p-2">
                    <ArrowRight className="w-5 h-5 text-gray-600 rotate-90" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Second Website URL
                  </label>
                  <input
                    type="text"
                    value={url2}
                    onChange={(e) => setUrl2(e.target.value)}
                    placeholder="e.g. competitor.com"
                    disabled={!canUseFeature || isAnalyzing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleCompare}
                  disabled={!canUseFeature || isAnalyzing || !url1.trim() || !url2.trim()}
                  className="w-full bg-black text-white font-medium py-4 px-6 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <GitCompare className="w-5 h-5" />
                      Compare Websites
                    </>
                  )}
                </button>

                {isAnalyzing && (
                  <div className="text-center text-sm text-gray-500 mt-4">
                    This may take up to 60 seconds as we analyze both websites
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
